import React, { useState, useEffect } from 'react';
import FourPillars from '../components/FourPillars';

const StoreClosingPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  const StepIcon = ({ step }) => {
    const iconSize = 140;
    const common = {
      width: iconSize,
      height: iconSize,
      viewBox: '0 0 140 140',
      fill: 'none',
    };
    
    switch (step) {
      case 1:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="40" y="40" width="60" height="60" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M50 55h40M50 70h40M50 85h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="90" cy="85" r="6" fill="currentColor"/>
          </svg>
        );
      case 2:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M35 50h70M35 70h70M35 90h50" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="90" cy="90" r="8" fill="currentColor"/>
          </svg>
        );
      case 3:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="45" width="70" height="50" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 60h50M45 75h50M45 90h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
          </svg>
        );
      case 4:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="35" width="70" height="70" rx="6" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 55h50M45 70h50M45 85h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M80 85l8 8 16-16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const steps = [
    {
      step: 1,
      title: 'Comprehensive Asset Assessment',
      description: 'We evaluate inventory, fixtures, equipment, and real estate assets to develop a customized liquidation strategy that maximizes recovery value.',
    },
    {
      step: 2,
      title: 'Strategic Liquidation Planning',
      description: 'Our team creates a detailed plan including pricing strategy, sales channels, timeline, and resource allocation optimized for your specific situation.',
    },
    {
      step: 3,
      title: 'On-Site Execution & Sales',
      description: 'Experienced merchandising professionals provide on-site support to manage product, fixtures, and equipment throughout the closure process.',
    },
    {
      step: 4,
      title: 'Final Settlement & Documentation',
      description: 'Complete asset disposition, final accounting, financial settlement, and comprehensive documentation for your records and compliance.',
    },
  ];

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'simplify-closings',
        'key-benefits',
        'industry-solutions',
        'documentation'
      ];
      
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <main className="services">
      <div className="container">
        <div className="sv-content-wrapper">
          <div className="sv-main-content">
            {/* Hero */}
            <section className="sv-hero">
              <div className="sv-eyebrow">Store Closing & Liquidation</div>
              <h1 className="sv-title">Store Closing & Liquidation</h1>
              <p className="sv-subtitle">Convert remaining inventory into working capital quickly during business transitions with our experienced merchandising team.</p>
            </section>

            {/* 4-Step Process Carousel */}
            <section className="ti-steps-carousel">
              <div className="ti-carousel-slide-container">
                <div 
                  className="ti-carousel-slides" 
                  style={{ transform: `translateX(-${currentStep * 100}%)` }}
                >
                  {steps.map((step, index) => (
                    <div key={step.step} className="ti-carousel-slide">
                      <div className="ti-carousel-icon">
                        <StepIcon step={step.step} />
                      </div>
                      <div className="ti-carousel-content">
                        <div className="ti-step-badge">STEP {step.step}</div>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ti-carousel-indicators">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    className={`ti-carousel-indicator ${index === currentStep ? 'active' : ''}`}
                    onClick={() => goToStep(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
            </section>

            {/* Main Content Section */}
            <section id="simplify-closings" className="sv-overview">
              <h2 className="sv-section-title">Simplify Store Closings with Professional Liquidation Support</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Store closures represent one of the most challenging transitions in retail operations. Every day of delay costs money through ongoing rent, utilities, payroll, and opportunity cost. Our comprehensive store closing service transforms this challenge into an opportunity to maximize recovery while minimizing operational disruption.
              </p>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Our experienced merchandising team handles every aspect of the closure process, from initial assessment through final settlement. We work with retailers, ecommerce fulfillment centers, restaurants, and specialty stores to convert remaining inventory, fixtures, and equipment into working capital quickly and efficiently.
              </p>
            </section>

            {/* Key Benefits */}
            <section id="key-benefits" className="sv-features">
              <h2 className="sv-section-title">Key Benefits</h2>
              <div className="sv-features-grid">
                <div className="sv-feature">
                  <h4>Maximize Recovery Value</h4>
                  <p>Strategic pricing and multi-channel sales maximize cash recovery while reducing closure timeline and operational costs.</p>
                </div>
                <div className="sv-feature">
                  <h4>Professional On-Site Support</h4>
                  <p>Experienced merchandising team provides on-site support to manage product, fixtures, and equipment throughout the closure process.</p>
                </div>
                <div className="sv-feature">
                  <h4>Complete Transparency</h4>
                  <p>Compliant and transparent reporting throughout the process with detailed documentation and regular status updates.</p>
                </div>
                <div className="sv-feature">
                  <h4>Flexible Strategies</h4>
                  <p>Tailored liquidation strategies designed for your specific situation, timeline, and recovery goals with flexible execution options.</p>
                </div>
              </div>
            </section>

            {/* Industry Solutions */}
            <section id="industry-solutions" className="sv-overview">
              <h2 className="sv-section-title">Industry-Specific Solutions</h2>
              <ul className="sv-bullets sv-list">
                <li>Retail Stores – Complete liquidation of merchandise, fixtures, and equipment for retail locations</li>
                <li>Ecommerce Fulfillment Centers – Close distribution centers and liquidate inventory and equipment</li>
                <li>Restaurants & Food Service – Dispose of food service equipment, fixtures, and remaining inventory</li>
                <li>Specialty Stores – Handle specialized inventory and equipment for niche retail operations</li>
                <li>Multi-Location Closures – Coordinate simultaneous closures across multiple locations</li>
              </ul>
            </section>

            {/* Documentation */}
            <section id="documentation" className="sv-overview">
              <h2 className="sv-section-title">Complete Documentation & Reporting</h2>
              <ul className="sv-bullets sv-list">
                <li>Initial Assessment Reports</li>
                <li>Liquidation Strategy Documentation</li>
                <li>Sales & Recovery Reports</li>
                <li>Financial Settlement Statements</li>
                <li>Asset Disposition Certificates</li>
                <li>Compliance Documentation</li>
              </ul>
            </section>

            {/* Four Pillars */}
            <FourPillars />

            {/* CTA */}
            <section className="sv-cta">
              <div className="sv-cta-inner">
                <h3>Need to plan a store closing?</h3>
                <p>We'll handle the details with transparency and speed to maximize your recovery.</p>
                <div className="sv-cta-actions">
                  <a className="btn btn-primary" href="tel:+18778739336">Call (877) 873-9336</a>
                  <a className="btn btn-secondary" href="mailto:info@globoflo.com">Email Us</a>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Links Sidebar */}
          <aside className="sv-quick-links">
            <h3 className="sv-quick-links-title">Store Closing & Liquidation</h3>
            <nav className="sv-quick-links-nav">
              <ul>
                <li>
                  <button 
                    className={activeSection === 'simplify-closings' ? 'active' : ''}
                    onClick={() => scrollToSection('simplify-closings')}
                  >
                    Simplify Store Closings with Professional Liquidation Support
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'key-benefits' ? 'active' : ''}
                    onClick={() => scrollToSection('key-benefits')}
                  >
                    Key Benefits
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'industry-solutions' ? 'active' : ''}
                    onClick={() => scrollToSection('industry-solutions')}
                  >
                    Industry-Specific Solutions
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'documentation' ? 'active' : ''}
                    onClick={() => scrollToSection('documentation')}
                  >
                    Complete Documentation & Reporting
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default StoreClosingPage;
